/**
 * @file Submission UI — publicationDetailsReadonly.
 * Publication Details — read-only data + field order for Dataset Overview.
 */

import type { FormField } from "../../interfaces/formInterfaces";
import {
  normalizeReferenceServiceLifeYears,
  resolveEpdProgrammeDisplayLabel,
  resolveSystemBoundaryDisplayLabel,
} from "../../constants/publicationEpdFieldOptions";
import { resolveLcaDatabaseDisplayLabel } from "../../constants/publicationLcaDatabaseOptions";
import { publicationFunctionalUnitUnitDisplay } from "../../pages/Dashboard/Contributor/Stages/Dataset/utils/publicationReadOnlyHelpers";
import {
  getStageOrderedFields,
  type StageFieldsSource,
} from "./backgroundStageFieldOrder";

/** Type definition — Publication Any Obj. */
export type PublicationAnyObj = Record<string, unknown>;

function valueForField(
  obj: PublicationAnyObj | null | undefined,
  fieldName: string
): unknown {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, fieldName)) return obj[fieldName];
  const camel = fieldName.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  if (Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  return undefined;
}

const PUBLICATION_FORM_ALIASES: Record<string, string[]> = {
  hasDataPublished: ["hasDataPublished", "has_data_published"],
  typeOfPublication: ["typeOfPublication", "type_of_publication"],
  correspondingAuthorEmail: [
    "correspondingAuthorEmail",
    "corresponding_author_email",
  ],
  uploadPublicationFile: [
    "uploadPublicationFile",
    "upload_publication_file",
    "publicationFileUrl",
    "publication_file_url",
    "fileUrl",
    "file_url",
  ],
  functionalUnitUnit: [
    "functionalUnitUnit",
    "functionalUnitUnitId",
    "functional_unit_unit",
    "functional_unit_unit_id",
  ],
  functionalUnitUnitId: [
    "functionalUnitUnitId",
    "functionalUnitUnit",
    "functional_unit_unit_id",
    "functional_unit_unit",
  ],
};

function functionalUnitUnitIdFromPublication(
  publication: PublicationAnyObj
): string {
  const nested = publication.functionalUnitUnit;
  if (nested && typeof nested === "object") {
    const o = nested as PublicationAnyObj;
    const id = o.id ?? o.unitId;
    if (id != null && id !== "") return String(id);
  }
  for (const key of [
    "functionalUnitUnitId",
    "functional_unit_unit_id",
    "functionalUnitUnit",
    "functional_unit_unit",
  ]) {
    const v = publication[key];
    if (v != null && v !== "" && typeof v !== "object") return String(v);
  }
  return "";
}

/** Exported function — read Publication Value For Form Field. */
export function readPublicationValueForFormField(
  obj: PublicationAnyObj | null | undefined,
  fieldName: string
): unknown {
  if (!obj) return undefined;
  const aliases = PUBLICATION_FORM_ALIASES[fieldName];
  if (aliases) {
    for (const key of aliases) {
      const v = obj[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return valueForField(obj, fieldName);
}

/** Exported function — build Publication Details Readonly Object. */
export function buildPublicationDetailsReadonlyObject(
  publication: PublicationAnyObj | null | undefined
): PublicationAnyObj | null {
  if (!publication || typeof publication !== "object") return null;

  const p = publication;
  const unitId = functionalUnitUnitIdFromPublication(p);

  const base: PublicationAnyObj = {
    hasDataPublished: readPublicationValueForFormField(p, "hasDataPublished") ?? "",
    typeOfPublication: readPublicationValueForFormField(p, "typeOfPublication") ?? "",
    correspondingAuthorEmail:
      readPublicationValueForFormField(p, "correspondingAuthorEmail") ?? "",

    publicationTitle: p.publicationTitle ?? "",
    publicationLink: p.publicationLink ?? "",
    doi: p.doi ?? "",
    journal: p.journal ?? "",
    volume: p.volume ?? "",
    conferenceName: p.conferenceName ?? "",
    organizationName: p.organizationName ?? "",

    knowPublicationDate: p.knowPublicationDate ?? "",
    publicationYear: p.publicationYear ?? "",
    publicationMonth: p.publicationMonth ?? "",
    publicationDay: p.publicationDay ?? "",

    uploadPublicationFile:
      readPublicationValueForFormField(p, "uploadPublicationFile") ?? "",

    product: p.product ?? "",
    relevantStandards: p.relevantStandards ?? "",
    epdRegistrationNumber: p.epdRegistrationNumber ?? "",

    epdPublicationYear: p.epdPublicationYear ?? "",
    epdPublicationMonth: p.epdPublicationMonth ?? "",
    epdPublicationDay: p.epdPublicationDay ?? "",

    validityYear: p.validityYear ?? "",
    validityMonth: p.validityMonth ?? "",
    validityDay: p.validityDay ?? "",

    geographicScopes: p.geographicScopes ?? "",
    country: p.country ?? "",
    epdProgramme: p.epdProgramme ?? "",
    complimentaryPcr: p.complimentaryPcr ?? "",
    productCategoryRules: p.productCategoryRules ?? "",
    lcaDatabasesUsed: p.lcaDatabasesUsed ?? "",
    lcaToolUsed: p.lcaToolUsed ?? "",
    systemBoundaries: p.systemBoundaries ?? "",
    cutOffCriteria: p.cutOffCriteria ?? "",
    allocationRules: p.allocationRules ?? "",

    productionPlantKnown: p.productionPlantKnown ?? "",
    productionPlantLatitude: p.productionPlantLatitude ?? "",
    productionPlantLongitude: p.productionPlantLongitude ?? "",
    productionPlantState: p.productionPlantState ?? "",
    productionPlantDistrict: p.productionPlantDistrict ?? "",
    productionPlantAddress: p.productionPlantAddress ?? "",
    productionPlantPincode: p.productionPlantPincode ?? "",

    functionalUnit: p.functionalUnit ?? "",
    functionalUnitUnit: unitId,
    functionalUnitUnitId: unitId,
    referenceServiceLife: p.referenceServiceLife ?? "",
  };

  for (const key of Object.keys(p)) {
    if (!Object.prototype.hasOwnProperty.call(p, key)) continue;
    const raw = p[key];
    if (raw == null || raw === "") continue;
    if (
      typeof raw === "string" ||
      typeof raw === "number" ||
      typeof raw === "boolean"
    ) {
      base[key] = String(raw);
    }
  }

  if (unitId) {
    base.functionalUnitUnit = unitId;
    base.functionalUnitUnitId = unitId;
  }

  const unitLabel = publicationFunctionalUnitUnitDisplay(p);
  if (unitLabel && !unitId) {
    base.functionalUnitUnit = unitLabel;
    base.functionalUnitUnitId = unitLabel;
  }

  return base;
}

/** Exported function — resolve Publication Stage Fields. */
export function resolvePublicationStageFields(
  formSchema: PublicationAnyObj[]
): FormField[] {
  const stage = formSchema.find((s) => s.code === "PUBLICATION_DETAILS");
  if (!stage) return [];
  return getStageOrderedFields(stage as StageFieldsSource);
}

/** Exported function — ordered Publication Form Fields. */
export function orderedPublicationFormFields(
  formSchema: PublicationAnyObj[]
): FormField[] {
  return resolvePublicationStageFields(formSchema).filter(
    (f) => f.isEnabled !== false
  );
}

function fileDisplayValue(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("/uploads/")) {
    const parts = s.split("/").filter(Boolean);
    return parts.length ? parts[parts.length - 1] : s;
  }
  return s;
}

/** Exported function — display Publication Field Value. */
export function displayPublicationFieldValue(
  field: FormField,
  data: PublicationAnyObj
): string {
  const raw = readPublicationValueForFormField(data, field.name);
  if (raw === null || raw === undefined) return "";

  if (field.type === "FILE") {
    return fileDisplayValue(raw);
  }

  if (field.type === "TEXT" && field.name === "referenceServiceLife") {
    return normalizeReferenceServiceLifeYears(raw);
  }

  if (field.type === "DROPDOWN") {
    if (field.name === "lcaDatabasesUsed") {
      return resolveLcaDatabaseDisplayLabel(raw);
    }
    if (field.name === "epdProgramme") {
      return resolveEpdProgrammeDisplayLabel(raw);
    }
    if (field.name === "systemBoundaries") {
      return resolveSystemBoundaryDisplayLabel(raw);
    }
    const options = field.dropdown?.options ?? field.dropdownValues ?? [];
    const match = options.find(
      (o) =>
        String(o.value ?? o.id ?? "") === String(raw) ||
        String(o.label ?? "") === String(raw)
    );
    if (match?.label) return String(match.label);
    return String(raw);
  }

  if (field.type === "UNIT") {
    const label = publicationFunctionalUnitUnitDisplay(data);
    if (label) return label;
  }

  return String(raw);
}
